<template>
  <div class="attached-files-wrapper">
    <template v-if="!loading">
      <em v-if="error" class="error">{{$t('error-attached-files')}}</em>

      <template v-else-if="attachedFiles.length > 0">
        <div class="file-list">
          <div v-if="attachedImageFiles.length > 0">{{ $t('image')}}</div>
          <div class="file-item" v-for="(file, index) in attachedImageFiles"  :key="file.id">
            <img 
              v-if="file.id" 
              :src="appendShortTermToken(`/api/attachedfile/${file.id}/thumbnail.json`, shortTermToken)" 
              alt="thumbnail" 
              class="thumbnail"
              @click="downloadFile(file)"
            />
            <div @click="downloadFile(file)" class="filename">{{file.filename}}</div>
            <div class="button-wrapper">
              <i v-if="sidebar" class="far fa-folder-open" @click="handleFileClick(file)" title="download"></i>
              <i v-if="sidebar" class="fas fa-times" @click="closeWindow(file)" title="close"></i>
              <i v-if="canEdit" class="fas fa-trash-alt" @click="confirmDeletion(file, index)" title="delete"></i>
            </div>
          </div>
          
          <div v-if="attachedNonImageFiles.length > 0">{{ $t('file')}}</div>
          <div class="file-item" v-for="(file, index) in attachedNonImageFiles"  :key="file.id">
            <div class="thumbnail etc-file"><i class="far fa-file"></i></div>
            <div @click="downloadFile(file)" class="filename">{{file.filename}}</div>
            <div class="button-wrapper">
              <i v-if="sidebar" class="fas fa-times" @click="closeWindow(file)" title="close"></i>
              <i v-if="canEdit" class="fas fa-trash-alt" @click="confirmDeletion(file, index)" title="delete"></i>
            </div>
          </div>
        </div>
      </template>

      <em v-else class="no-files">{{$t('no-attached-file')}}</em>

      <button v-if="canEdit" class="button" @click="displayModal()">
        {{$t('button-add')}}
      </button>
    </template>
  </div>
</template>

<script>
import {Cytomine, AttachedFileCollection} from 'cytomine-client';
import AttachedFileModal from './AttachedFileModal';
import {appendShortTermToken} from '@/utils/token-utils.js';
import {get} from '@/utils/store-helpers.js';

export default {
  name: 'attached-files',
  props: {
    object: {type: Object},
    canEdit: {type: Boolean, default: true},
    sidebar: {type: Boolean, default: false},
  },
  data() {
    return {
      loading: true,
      error: false,
      attachedFiles: [],
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    host() {
      return Cytomine.instance.host;
    },
    attachedImageFiles() {
      return this.attachedFiles.filter(file => file.thumbnailPath !== null);
    },
    attachedNonImageFiles() {
      return this.attachedFiles.filter(file => file.thumbnailPath === null);
    }
  },
  methods: {
    appendShortTermToken,
    displayModal() {
      // required to use programmatic modal because the description is sometimes displayed in elements with a
      // CSS transform (e.g. popover) that conflict with the fixed position of the modal
      // (http://meyerweb.com/eric/thoughts/2011/09/12/un-fixing-fixed-elements-with-css-transforms/)

      this.$buefy.modal.open({
        parent: this,
        component: AttachedFileModal,
        props: {object: this.object},
        hasModalCard: true,
        canCancel: false,
        events: {'addAttachedFile': this.addAttachedFile}
      });
    },
    downloadAttachedFile(host, attachedFile) {
      const url = appendShortTermToken(
        host + attachedFile.url,
        this.shortTermToken
      );
      window.open(url, '_blank'); // 새 창에서 열기
    },
    addAttachedFile(attachedFile) {
      this.attachedFiles.push(attachedFile);
    },
    confirmDeletion(attachedFile) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-attached-file', {filename: attachedFile.filename}),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteAttachedFile(attachedFile)
      });
    },
    async deleteAttachedFile(attachedFile) {
      try {
        this.closeWindow(attachedFile);

        await attachedFile.delete();

        const index = this.attachedFiles.findIndex(
          f => f.id === attachedFile.id
        );

        if (index !== -1) {
          this.attachedFiles.splice(index, 1);
        }

        this.$notify({
          type: 'success',
          text: this.$t('notif-success-attached-file-deletion', {
            filename: attachedFile.filename
          })
        });
      }
      catch (error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-attached-file-deletion', {
            filename: attachedFile.filename
          })
        });
      }
    },
    closeWindow(file) {
      this.$eventBus.$emit('closeImageWindow', file);
    },
    handleFileClick(file) {
      if(file.thumbnailPath) {
        this.openImageWindow(file);
      }
    },
    downloadFile(file) {
      this.downloadAttachedFile(this.host, file);
    },
    openImageWindow(file) {
      // 이미지 URL 준비
      const url = `/api/attachedfile/${file.id}/download`;

      // 뷰어에 전달
      this.$eventBus.$emit('openImageWindow', { fileName: file.filename, fileUrl: url, id: file.id });
    }
  },
  async created() {
    try {
      this.attachedFiles = (await AttachedFileCollection.fetchAll({object: this.object})).array;
    }
    catch(error) {
      console.log(error);
      this.error = true;
    }
    this.loading = false;
  }

};
</script>

<style scoped>
.attached-files-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

.file-item {
  width: 100%;
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 4px 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  cursor: pointer;
}

.thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  margin-right: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
}
.thumbnail.etc-file {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
}

.filename {
  flex: 1;
  cursor: pointer;
  color: #4a4a4a;
  text-decoration: none;

  white-space: normal;      /* 기본 줄바꿈 허용 */
  word-break: break-word;   /* 단어 단위로 줄바꿈 가능 */
}

.filename:hover {
  text-decoration: underline;
}

.button-wrapper {
  display: flex;
  /* flex-direction: column; */
  height: 100%;
  /* justify-content: space-between; */
  justify-content: flex-start;
  gap: 7px;
  padding: 5px;
}

.delete-button {
  background: none;
  border: none;
  color: #ff5c5c;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  font-size: 0.9em;
}

.delete-button:hover {
  color: #ff0000;
}

.error, .no-files {
  color: #999;
  font-style: italic;
}
</style>